import {
  Button,
  Col,
  Container,
  Form,
  FormControl,
  InputGroup,
  OverlayTrigger,
  Row,
  Tooltip,
} from 'react-bootstrap'
import {
  AttributeType,
  fetchAllTags,
  TagAttribute,
  TagAttributeType,
  TagAvailableValue,
  TagModel,
} from '@editor/store/slices/tag.slice'
import React, { useContext, useEffect, useState } from 'react'
import { FieldValue, FieldValues, useForm } from 'react-hook-form'
import {
  EditorContext,
  EditorContextType,
} from '@editor/components/editorContext'

const TagForm = ({
  editingTag,
  editingIndex,
  submitTagForm,
  closeForm,
}: {
  editingTag: TagModel | undefined
  editingIndex?: string
  submitTagForm: (newTag: TagModel) => void
  closeForm: (e: React.MouseEvent) => void
}) => {
  const { register, setValue, handleSubmit, resetField } = useForm()
  const { tags, dispatch } = useContext(EditorContext) as EditorContextType
  const [correspondingTagAttributeList, setCorrespondingTagAttributeList] =
    useState<TagAttribute[]>([])
  const [editingTagAttributeList, setEditingTagAttributeList] = useState<
    TagAttribute[]
  >([])
  const [selectedTagAttributes, setSelectedTagAttributes] = useState<
    {
      index: string
      attribute: TagAttribute | undefined
    }[]
  >([])
  const [correspondingTag, setCorrespondingTag] = useState<
    TagModel | undefined
  >()
  const [tagTypes, setTagTypes] = useState<{ index: string; value: string }[]>(
    []
  )
  const deleteAttributeName = '--smazat--'

  useEffect(() => {
    if (tags.length === 0) {
      dispatch(fetchAllTags())
    }
  }, [])

  useEffect(() => {
    setCorrespondingTag(
      editingTag
        ? tags.find((tag) => tag.tagName === editingTag.tagName)
        : undefined
    )
    setEditingTagAttributeList(editingTag ? editingTag.tagAttributes : [])
    setCorrespondingTagAttributeList([])
  }, [editingTag])

  useEffect(() => {
    if (!editingTag) {
      setEditingTagAttributeList([])
    }
    setCorrespondingTagAttributeList(
      correspondingTag ? correspondingTag.tagAttributes : []
    )
    setSelectedTagAttributes([])
    tagTypes.forEach((tagType) => resetField(tagType.index))
    selectedTagAttributes.forEach((attribute, index) =>
      resetFieldsByIndex(index.toString())
    )
  }, [correspondingTag])

  useEffect(() => {
    if (!tags || !tags.length) {
      dispatch(fetchAllTags())
    }
  }, [dispatch])

  const handleFormSubmit = (data: FieldValue<FieldValues>) => {
    if (correspondingTag) {
      const tagAttribute = selectedTagAttributes.reduce(
        (accumulator: TagAttribute[], tagAttribute, index) => {
          const attrName = data[`${index}-attribute`]
          if (attrName === deleteAttributeName) return accumulator
          let attrValue
          if (data[`${index}-type`].includes(AttributeType.STRING)) {
            attrValue = data[`${index}-value`]
          } else if (
            data[`${index}-type`].includes(AttributeType.WITH_UNITES)
          ) {
            attrValue =
              data[`${index}-value`] +
              data[`${index}-type`]?.replace(
                `${AttributeType.WITH_UNITES}-`,
                ''
              )
          } else {
            attrValue = data[`${index}-type`]
          }

          accumulator.push({
            attributeName: attrName,
            attributeValue: attrValue,
          } as TagAttribute)
          return accumulator
        },
        []
      )
      const tag: TagModel = {
        id: '',
        tagName: correspondingTag?.tagName,
        tagDescription: '',
        tagAttributes: tagAttribute,
      }
      submitTagForm(tag)
    }
  }

  const resetFieldsByIndex = (index: string) => {
    resetField(`${index}-attribute`)
    resetField(`${index}-type`)
    resetField(`${index}-value`)
  }
  const handleAddAttribute = (e: React.MouseEvent) => {
    e.preventDefault()
    setEditingTagAttributeList([{} as TagAttribute, ...editingTagAttributeList])
  }

  const onTagSelectChange = (e: React.ChangeEvent, newValue: string) => {
    e.preventDefault()
    setValue('tagSelect', newValue)
    setCorrespondingTag(tags.find((tag) => tag.tagName === newValue))
    setSelectedTagAttributes([])
  }

  const onTypeSelectChange = (
    newValue: string,
    formControlName: string,
    e?: React.ChangeEvent
  ) => {
    e?.preventDefault()
    setValue(formControlName, newValue)
    setTagTypes((prevState) => {
      const found = prevState?.find((el) => el.index === formControlName)
      if (found) {
        // If found, replace the value of the found element
        return prevState.map((el) =>
          el.index === formControlName ? { ...el, value: newValue } : el
        )
      } else {
        return [{ index: formControlName, value: newValue }, ...prevState]
      }
    })
  }

  const onAttrSelectChange = (
    newValue: string,
    formControlName: string,
    e?: React.ChangeEvent
  ) => {
    e?.preventDefault()
    setValue(formControlName, newValue)
    const newAttribute = correspondingTag?.tagAttributes.find(
      (tagAttribute) => tagAttribute.attributeName === newValue
    )
    setSelectedTagAttributes((prevState) => {
      const found = prevState?.find((el) => el.index === formControlName)
      if (found) {
        return prevState.map((el) =>
          el.index === formControlName ? { ...el, attribute: newAttribute } : el
        )
      } else {
        return [
          { index: formControlName, attribute: newAttribute },
          ...prevState,
        ]
      }
    })
    const tagTypeIndex = formControlName.replace('attribute', 'type')
    setTagTypes(tagTypes.filter((item) => item.index !== tagTypeIndex))
  }

  const getTagType = (
    index: string
  ): { index: string; value: string } | undefined => {
    return tagTypes.find((type) => type.index === `${index}-type`)
  }
  const hasTagTypeInput = (
    tagType: { index: string; value: string } | undefined
  ): boolean => {
    return (
      !!tagType &&
      (tagType.value.startsWith(AttributeType.WITH_UNITES) ||
        tagType.value.startsWith(AttributeType.STRING))
    )
  }

  const getTagTypeUnits = (
    tagType: { index: string; value: string } | undefined
  ): string | undefined => {
    if (tagType && tagType.value.startsWith(AttributeType.WITH_UNITES)) {
      return tagType.value.replace(AttributeType.WITH_UNITES + '-', '')
    } else return undefined
  }

  const getCorrespondingTagAttribute = (
    index: string
  ): TagAttribute | undefined => {
    return selectedTagAttributes?.find(
      (entry) => `${index}-attribute` === entry.index
    )?.attribute
  }

  const estimateTypeByValue = (
    correspondingTag: TagAttribute,
    value: string
  ): string => {
    const possibleUnit = value.slice(-2)
    let selectValueFound: string | undefined = undefined
    correspondingTag.attributeType.forEach(
      (tagType: TagAttributeType) =>
        tagType.availableValues?.forEach((tagValue: TagAvailableValue) => {
          if (!!tagValue.unit && possibleUnit.includes(tagValue.unit)) {
            selectValueFound = `${AttributeType.WITH_UNITES.toString()}-${
              tagValue.unit
            }`
          } else if (!!tagValue.value && tagValue.value === value) {
            selectValueFound = tagValue.value
          }
        })
    )
    return !!selectValueFound
      ? selectValueFound
      : AttributeType.STRING.toString()
  }

  const getDefaultValue = (value: string, units?: string): string => {
    return units && value ? value.split(units).join('') : value
  }

  const TagAttributeValueFormGroup = ({
    tagAttribute,
    index,
  }: {
    tagAttribute: TagAttribute
    index: string
  }) => {
    const tagType = getTagType(index)
    const isInputVisible = hasTagTypeInput(tagType)
    const units = getTagTypeUnits(tagType)
    return (
      <>
        {tagTypes?.length > 0 && isInputVisible && (
          <InputGroup>
            <FormControl
              {...register(`${index}-value`)}
              placeholder="Vyplňte hodnotu"
              defaultValue={
                editingTag
                  ? getDefaultValue(tagAttribute.attributeValue, units)
                  : ''
              }
            ></FormControl>
            {units && <InputGroup.Text>{units}</InputGroup.Text>}
          </InputGroup>
        )}
      </>
    )
  }

  const TagAttributeTypeFormGroup = ({
    tagAttribute,
    index,
  }: {
    tagAttribute: TagAttribute
    index: string
  }) => {
    const correspondingTagAttribute = getCorrespondingTagAttribute(index)
    if (
      !!tagAttribute &&
      Object.keys(tagAttribute).length > 0 &&
      correspondingTagAttribute &&
      !tagTypes.some((type) => type.index === `${index}-type`)
    ) {
      const attributeTypeSelectValue: string = estimateTypeByValue(
        correspondingTagAttribute,
        tagAttribute.attributeValue
      )
      onTypeSelectChange(attributeTypeSelectValue, `${index}-type`)
    }
    return (
      <>
        <InputGroup>
          <InputGroup.Text>Typ</InputGroup.Text>
          <Form.Control
            {...register(`${index}-type`)}
            as="select"
            onChange={(event) =>
              onTypeSelectChange(event.target.value, `${index}-type`, event)
            }
            defaultValue={'Vyberte variantu'}
          >
            <option disabled={true}>Vyberte variantu</option>
            {correspondingTagAttribute?.attributeType?.map(
              (attrType: TagAttributeType) => (
                <>
                  {attrType.attributeType === AttributeType.STRING && (
                    <option value={AttributeType.STRING.toString()}>
                      TEXT
                    </option>
                  )}
                  {attrType.attributeType === AttributeType.SELECT && (
                    <>
                      {attrType.availableValues?.map((value, index) => (
                        <option key={index} value={value.value}>
                          {value.value}
                        </option>
                      ))}
                    </>
                  )}
                  {attrType.attributeType === AttributeType.WITH_UNITES && (
                    <>
                      {attrType.availableValues?.map((value, index) => (
                        <option
                          value={`${AttributeType.WITH_UNITES.toString()}-${
                            value.unit
                          }`}
                          key={index}
                        >
                          HODNOTA v {value.unit}
                        </option>
                      ))}
                    </>
                  )}
                </>
              )
            )}
          </Form.Control>
        </InputGroup>
      </>
    )
  }
  const TagAttributeFormGroup = ({
    tagAttribute,
    index,
  }: {
    tagAttribute: TagAttribute
    index: string
  }) => {
    const formControlName = `${index}-attribute`
    const correspondingTagAttribute = getCorrespondingTagAttribute(index)
    if (
      editingTag &&
      !selectedTagAttributes.some((attr) => attr.index === formControlName)
    ) {
      onAttrSelectChange(tagAttribute.attributeName, formControlName)
    }
    return (
      <Row className="my-3">
        <Row>
          <Col xs={6}>
            {correspondingTag && (
              <InputGroup>
                <InputGroup.Text>Atr</InputGroup.Text>
                <Form.Control
                  {...register(formControlName)}
                  as="select"
                  onChange={(event) =>
                    onAttrSelectChange(
                      event.target.value,
                      `${index}-attribute`,
                      event
                    )
                  }
                  defaultValue={'--Vyberte atribut--'}
                >
                  <option disabled={true}>--Vyberte atribut--</option>
                  {correspondingTagAttributeList.map(
                    (tagAttribute: TagAttribute, index) => (
                      <option key={index} value={tagAttribute.attributeName}>
                        {tagAttribute.attributeName}
                      </option>
                    )
                  )}
                  <option value={undefined}>{deleteAttributeName}</option>
                </Form.Control>
                {correspondingTagAttribute && (
                  <OverlayTrigger
                    placement="right"
                    overlay={
                      <Tooltip id="select-tooltip">
                        {correspondingTagAttribute?.attributeDescription}
                      </Tooltip>
                    }
                  >
                    <InputGroup.Text>?</InputGroup.Text>
                  </OverlayTrigger>
                )}
              </InputGroup>
            )}
          </Col>
          {correspondingTagAttribute && (
            <Col>
              <TagAttributeTypeFormGroup
                tagAttribute={tagAttribute}
                index={index}
                key={'attr-' + index}
              ></TagAttributeTypeFormGroup>
            </Col>
          )}
        </Row>
        <Row>
          {correspondingTagAttribute && (
            <Col xs={6}>
              <TagAttributeValueFormGroup
                tagAttribute={tagAttribute}
                index={index}
                key={'val-' + index}
              ></TagAttributeValueFormGroup>
            </Col>
          )}
        </Row>
      </Row>
    )
  }

  const TagAttributeFormList = () => (
    <>
      {editingTagAttributeList &&
        editingTagAttributeList.map((tagAttribute, index) => (
          <TagAttributeFormGroup
            key={index}
            tagAttribute={tagAttribute}
            index={index.toString()}
          ></TagAttributeFormGroup>
        ))}
      <Row className="ms-1 mt-3 ">
        {correspondingTagAttributeList.length > 0 && (
          <Button
            variant="success"
            size="sm"
            className="w-auto fw-bold"
            onClick={(e) => handleAddAttribute(e)}
          >
            Přidat attribut
          </Button>
        )}
      </Row>
    </>
  )

  const TagFormGroup = () => (
    <>
      <InputGroup>
        <InputGroup.Text>Tag</InputGroup.Text>
        <Form.Control
          {...register('tagSelect')}
          as="select"
          disabled={false}
          onChange={(event) => onTagSelectChange(event, event.target.value)}
          defaultValue={'Vyberte tag'}
        >
          <option disabled={true}>Vyberte tag</option>
          {tags.map((tag: TagModel, index) => (
            <option key={index} value={tag.tagName}>
              {tag.tagName}
            </option>
          ))}
        </Form.Control>
      </InputGroup>
    </>
  )

  return (
    <Form>
      <Container className="bg-light-subtle rounded p-2 m-0 mt-4">
        <Row className="mb-2">
          <h5 className="m-0">
            {editingTag ? `Úprava tagu ${editingTag?.tagName}:` : 'Nový tag:'}
          </h5>
        </Row>
        {editingIndex && editingTag ? (
          <></>
        ) : (
          <Row className="my-1">
            <TagFormGroup></TagFormGroup>
          </Row>
        )}
        <TagAttributeFormList />
        <Row>
          <Col></Col>
          <Col className="d-flex justify-content-end mt-2">
            <Button
              variant="outline-danger"
              className="w-auto fw-bold"
              type="submit"
              onClick={closeForm}
            >
              Zavřít
            </Button>
            <Button
              variant="primary"
              className="ms-1 w-auto fw-bold"
              type="submit"
              onClick={handleSubmit(handleFormSubmit)}
            >
              {!!editingTag ? 'Upravit' : 'Vytvořit'}
            </Button>
          </Col>
        </Row>
      </Container>
    </Form>
  )
}
export default TagForm
